/**
 * Sales pricing breakdown — the salesperson-facing view of a rate/pricing line.
 *
 * Leads with **ocean all-in per container** (FRT + `category == "ocean"`
 * per-container surcharges only), then surfaces the remaining **locals**
 * (every `category == "local"` per-container charge: THC/THD, CRS, CLS, …) and
 * **per-B/L** charges as add-ons sitting *beside* the hero number rather than
 * folded into it. A side **total** reconstructs the full shipment cost.
 *
 * The split is driven by the surcharge `category` (not the FRT++ THC/THD
 * heuristic) so a non-terminal local charge such as CRS is reported under
 * locals instead of silently inflating the ocean all-in.
 *
 * Invariant (when data is complete): `oceanAllIn + localsPerContainer`
 * equals the backend `price_net` (all same-currency per-container surcharges on
 * top of FRT), so the side total matches `price_net * qty + per-BL`.
 *
 * Mirrors the render rules in `surchargeDisplay.ts`: CNY and inclusive lines
 * are hidden, per-TEU amounts scale by equipment size, and per-B/L amounts
 * never enter the per-container figure.
 */

import {
  displayFrtPp,
  effectiveSurchargeAmount,
  isBlBasis,
  isTerminalHandlingSurcharge,
  surchargesForLineDisplay,
  normSurchargeBasis,
  type FrtPpSurcharge,
} from "./surchargeDisplay";

/** A surcharge usable by the sales breakdown (adds an optional display code). */
export interface SalesSurcharge extends FrtPpSurcharge {
  code?: string | null;
}

/** A single surcharge rendered as a chip beside the hero price. */
export interface SalesSurchargeChip {
  code: string;
  amount: number;
}

export interface SalesPriceBreakdown {
  currency: string;
  /** FRT++ per container: ocean all-in excluding THC/THD and per-B/L. */
  oceanAllIn: number;
  /** Per-container local charges excluded from FRT++ (THC/THD, …). */
  localsPerContainer: number;
  /** Local add-on chips (code + effective per-container amount). */
  localChips: SalesSurchargeChip[];
  /** One-off per-B/L (and per-shipment) charges computed from the line. */
  blOnce: number;
  /** Per-B/L add-on chips (code + amount). */
  blChips: SalesSurchargeChip[];
  /** Per-B/L amount used for the total (override when provided, else `blOnce`). */
  blForTotal: number;
  quantity: number;
  /** `(oceanAllIn + localsPerContainer) * qty + blForTotal`. */
  total: number;
}

/** Per-container sell price: ocean + local surcharges, excluding per-B/L. */
export function perContainerSellPrice(
  breakdown: Pick<SalesPriceBreakdown, "oceanAllIn" | "localsPerContainer">,
): number {
  return breakdown.oceanAllIn + breakdown.localsPerContainer;
}

export interface SalesPriceInput {
  frt: number;
  currency: string;
  equipment_size?: string | null;
  surcharges?: SalesSurcharge[] | null;
  frt_pp?: number | null;
  frt_pp_overlaid?: number | null;
  /** Container quantity on this line; defaults to 1. */
  quantity?: number;
  /**
   * Allocated per-B/L charge to use for the total and display instead of the
   * line's own per-B/L surcharge sum (e.g. a booking's `bl_charge` share).
   */
  blOverride?: number | null;
}

function isBlOrShipmentBasis(basis?: string | null): boolean {
  const norm = normSurchargeBasis(basis);
  return norm === "bl" || norm === "shipment";
}

function surchargeCode(s: SalesSurcharge, fallback: string): string {
  return (s.code ?? "").trim() || fallback;
}

/**
 * Compute the sales-facing breakdown for one rate/pricing line.
 *
 * `oceanAllIn` reuses `displayFrtPp` (overlay → API `frt_pp` → recompute), so
 * notice overlays and partial payloads are handled consistently with the rest
 * of the rate UI.
 */
export function salesPriceBreakdown(input: SalesPriceInput): SalesPriceBreakdown {
  const {
    frt,
    currency,
    equipment_size: equipmentSize,
    surcharges,
    frt_pp,
    frt_pp_overlaid,
    quantity = 1,
    blOverride,
  } = input;

  const lineCurrency = currency.toUpperCase();
  const renderable = surchargesForLineDisplay<SalesSurcharge>(
    surcharges ?? [],
  );

  // Split per-container surcharges into ocean vs local by their `category` so a
  // local charge carried on a non-THC code (e.g. CRS) surfaces under locals
  // instead of being folded into the ocean all-in. Only same-currency,
  // non-inclusive, per-container/TEU charges participate; per-B/L amounts are
  // handled separately below. The sum `oceanAllIn + localsPerContainer` still
  // equals the backend `price_net` (FRT + every per-container surcharge).
  const localChips: SalesSurchargeChip[] = [];
  let localsPerContainer = 0;
  let oceanSurcharges = 0;
  for (const s of renderable) {
    if (s.category === "inclusive") continue;
    if ((s.currency ?? "").toUpperCase() !== lineCurrency) continue;
    const basis = normSurchargeBasis(s.basis);
    if (basis !== "container" && basis !== "teu") continue;
    const amount = effectiveSurchargeAmount(s, equipmentSize);
    // `local` → locals add-on; `ocean` → ocean all-in. Uncategorized rows fall
    // back to the FRT++ heuristic (terminal handling → locals, everything else
    // → ocean) so legacy lines without a category never drop a charge from
    // either bucket and the net invariant holds.
    const isLocal =
      s.category === "local" ||
      (s.category == null && isTerminalHandlingSurcharge(s));
    if (isLocal) {
      localsPerContainer += amount;
      localChips.push({ code: surchargeCode(s, "Local"), amount });
    } else {
      oceanSurcharges += amount;
    }
  }

  // Recompute ocean all-in from the ocean-category surcharges. Only fall back to
  // the API `frt_pp` (or overlay) when the line arrived without any renderable
  // surcharge detail, so a partial payload still fills the column.
  const oceanAllIn =
    renderable.length > 0
      ? frt + oceanSurcharges
      : displayFrtPp({
          frt,
          currency,
          equipment_size: equipmentSize,
          surcharges: surcharges ?? [],
          frt_pp,
          frt_pp_overlaid,
        });

  // Per-B/L (and per-shipment) one-off charges.
  const blChips: SalesSurchargeChip[] = [];
  let blOnce = 0;
  for (const s of renderable) {
    if (s.category === "inclusive") continue;
    if ((s.currency ?? "").toUpperCase() !== lineCurrency) continue;
    if (!isBlOrShipmentBasis(s.basis)) continue;
    blOnce += s.amount;
    blChips.push({
      code: surchargeCode(s, isBlBasis(s.basis) ? "B/L" : "Shipment"),
      amount: s.amount,
    });
  }

  const blForTotal = blOverride ?? blOnce;
  const perContainer = oceanAllIn + localsPerContainer;
  const total = perContainer * quantity + blForTotal;

  return {
    currency,
    oceanAllIn,
    localsPerContainer,
    localChips,
    blOnce,
    blChips,
    blForTotal,
    quantity,
    total,
  };
}

const MONEY_EPS = 0.005;

function moneyEq(a: number, b: number): boolean {
  return Math.abs(a - b) < MONEY_EPS;
}

export interface HeroBlDisplay {
  /** Per-B/L rate from the pricing line surcharges (`blOnce`). */
  showPerBl: boolean;
  perBlAmount: number;
  showBlComponents: boolean;
  blChips: SalesSurchargeChip[];
}

/**
 * Hero pricing: line per-B/L rate (+ itemized BL surcharges when useful).
 * Booking `bl_charge` belongs in the shipment-total recipe only.
 */
export function resolveHeroBlDisplay(
  breakdown: Pick<SalesPriceBreakdown, "blOnce" | "blChips">,
): HeroBlDisplay {
  const linePerBl = breakdown.blOnce;
  const chips = breakdown.blChips;

  const showBlComponents =
    chips.length > 1 ||
    (chips.length === 1 &&
      linePerBl > 0 &&
      !moneyEq(chips[0].amount, linePerBl));

  return {
    showPerBl: linePerBl > 0,
    perBlAmount: linePerBl,
    showBlComponents,
    blChips: showBlComponents ? chips : [],
  };
}
